'use client'

import { useAuthStore } from '@/lib/stores/auth-store'
import { type LoginType } from '@/lib/auth'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { DEFAULT_ROUTE } from "@/lib/constants";
import { isCurrentAppRoute, navigateToAppRoute, redirectToAppRoute } from '@/lib/navigation'

const resolvedEnvLoginType = resolveEnvLoginType()

function resolveEnvLoginType(): LoginType | null {
  const envValue = process.env.NEXT_PUBLIC_LOGIN_TYPE?.trim().toLowerCase()
  if (envValue === 'remote') {
    return 'remote'
  }
  if (envValue === 'local') {
    return 'local'
  }
  return null
}

export function useAuth() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const {
    isAuthenticated,
    isLoading,
    login,
    logout,
    setToken,
    checkAuth,
    checkAuthRequired,
    error,
    hasHydrated,
    authRequired,
    loginType: storedLoginType,
    setLoginType
  } = useAuthStore()

  useEffect(() => {
    // Only check auth after the store has hydrated from localStorage
    if (hasHydrated) {
      // First check if auth is required
      if (authRequired === null) {
        checkAuthRequired().then((required) => {
          // If auth is required, check if we have valid credentials
          if (required) {
            checkAuth()
          }
        })
      } else if (authRequired) {
        // Auth is required, check credentials
        checkAuth()
      }
      // If authRequired === false, we're already authenticated (set in checkAuthRequired)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, authRequired])

  useEffect(() => {
    if (resolvedEnvLoginType && resolvedEnvLoginType !== storedLoginType) {
      setLoginType(resolvedEnvLoginType)
    }
  }, [setLoginType, storedLoginType])

  const activeLoginType = resolvedEnvLoginType ?? storedLoginType

  const handleLogin = async (password: string) => {
    // 登录前清除所有缓存数据，确保切换租户后不会显示旧数据
    queryClient.clear()
    console.log('[useAuth] handleLogin called, activeLoginType:', activeLoginType)

    let success = false
    if (activeLoginType === 'remote') {
      success = await setToken(password) // 远程登录直接保存token
      console.log('[useAuth] setToken result:', success)
    } else {
      success = await login(password)
      console.log('[useAuth] login result:', success)
    }

    if (success) {
      // Check if there's a stored redirect path
      const redirectPath = sessionStorage.getItem('redirectAfterLogin')
      console.log('[useAuth] redirectAfterLogin:', redirectPath)

      const targetPath = redirectPath || DEFAULT_ROUTE

      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin')
      }

      // Use router.push for SPA navigation; fall back to a document navigation
      // when the route does not change.
      try {
        console.log('[useAuth] router.push to:', targetPath)
        navigateToAppRoute(router, targetPath)

        // Safety net: if the page hasn't navigated within 1s, force a full reload.
        // This handles edge cases in Tauri webviews where router.push may be a no-op.
        setTimeout(() => {
          if (!isCurrentAppRoute(targetPath)) {
            console.warn('[useAuth] router.push may not have navigated, force redirect to:', targetPath)
            redirectToAppRoute(targetPath)
          }
        }, 1000)
      } catch (err) {
        console.error('[useAuth] router.push failed, using window.location.href:', err)
        redirectToAppRoute(targetPath)
      }
    }
    return success
  }

  const handleLogout = () => {
    logout()
    // 登出时清除所有缓存数据
    queryClient.clear()
  }

  return {
    isAuthenticated,
    isLoading: isLoading || !hasHydrated, // Treat lack of hydration as loading
    error,
    login: handleLogin,
    logout: handleLogout
  }
}
